import { useTranslations } from "next-intl";
import { useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import { Typeahead, TypeaheadItem } from "@zapier/design-system";

import { useZapListing, Zap } from "@zapier/reporting/src/hooks/useZapListing";

const LOAD_MORE_KEY = "__load_more__";
const LOADING_KEY = "__loading__";

type Props = {
  accountId?: string;
  /** Pass to render selected items outside of dropdown */
  children?: (props: {
    selectedZaps: Zap[];
    zapIds: string[];
  }) => React.ReactElement[];
  id?: string;
  menuWidth?: string;
  onChange: (value: string | undefined) => void;
  value: string[] | undefined;
};

type ZapItem = Zap & { name: string; __isLoadMore?: boolean };

function toItem(zap: Zap): ZapItem {
  return { ...zap, name: zap.title || `Name your zap (#${zap.id})` };
}

function ZapFilter(props: Props) {
  const t = useTranslations("history-page.filters.zap-filter");
  const tCommon = useTranslations("common");
  const zapIds = props.value || [];
  const [inputValue, setInputValue] = useState("");
  const { onChange } = props;
  const { isLoading, isLoadingMore, onLoadMore, onSearch, selectedZaps, zaps } =
    useZapListing({ accountId: props.accountId, zapIds });

  const showLoadMore = Boolean(onLoadMore) && !isLoadingMore;

  const allItems: ZapItem[] = [
    ...zaps.map(toItem),
    ...(isLoadingMore
      ? [
          {
            id: LOADING_KEY,
            name: tCommon("loading"),
            __isLoadMore: true,
          } as ZapItem,
        ]
      : showLoadMore
        ? [
            {
              id: LOAD_MORE_KEY,
              name: tCommon("load-more"),
              __isLoadMore: true,
            } as ZapItem,
          ]
        : []),
  ];

  return (
    <>
      <Typeahead
        aria-label={t("search-aria-label")}
        iconLeft="miscBoltAlt"
        isBusy={isLoading}
        id={props.id}
        inputValue={inputValue}
        items={allItems}
        menuTrigger="focus"
        menuWidth={props.menuWidth}
        onInputChange={(v) => {
          setInputValue(v);
          onSearch(v);
        }}
        onSelectionChange={(key) => {
          if (key === LOAD_MORE_KEY) {
            onLoadMore?.();
            return;
          }
          if (key === LOADING_KEY) return;
          onChange(key ? String(key) : undefined);
          setInputValue("");
        }}
        placeholder={t("placeholder")}
        selectedKey={null}
        size="medium"
      >
        {(dsItem) => {
          const item = dsItem as ZapItem;
          return (
            <TypeaheadItem
              id={item.id}
              isDisabled={item.id === LOADING_KEY}
              textValue={item.name}
            >
              {item.__isLoadMore ? (
                item.name
              ) : (
                <>
                  <Icon
                    name={
                      item.kind === "transfer" ? "arrowConnect" : "miscBoltAlt"
                    }
                    size={24}
                  />
                  {item.name}
                </>
              )}
            </TypeaheadItem>
          );
        }}
      </Typeahead>
      {props.children && props.children({ selectedZaps, zapIds })}
    </>
  );
}

export default ZapFilter;
